/**
 * WHAT EXISTS — the entities and fields the entity chat's model reads (spec Q29: no cap in V1).
 * Parity (ADR 0009): hidden entities are left out because the page hides them; system fields are left out
 * because the page shows them read-only and the chat may not change them. No ids anywhere: a link names
 * the entity it points at.
 * ⚠ `import type` only — see rules/entity-form-rules.ts.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';
import type { OotbInfo, PermissionWord, RoleInfo } from '../zs-entity.types';
import { isFullAccessRole, isSystemField, sampleRowFields } from '../rules/entity-form-rules';

export interface SummaryField {
  name: string;
  type: string;
  /** For a link: the linked entity's NAME. */
  reference: string | null;
  description: string | null;
  /** For a list of items: its own fields. */
  fields: SummaryField[];
}

export interface SummaryEntity {
  name: string;
  status: string;
  fields: SummaryField[];
  /** LOCAL entities only: how many sample rows, and the first 10. */
  rowCount: number;
  rows: Record<string, unknown>[];
  /** The real sample-row columns (plan 4a, MINOR B) — a row may carry a leftover key that is not one. */
  rowColumns: string[];
}

export function buildEntitySummary(entities: readonly Entity[]): SummaryEntity[] {
  const nameById = new Map(entities.map((e) => [e.id, e.name]));
  const fieldsOf = (data: readonly TypeData[] | undefined): SummaryField[] =>
    (data ?? [])
      .filter((f) => !isSystemField(f.name))
      .map((f) => ({
        name: f.name,
        type: f.type,
        // Was (2026-10-03, final review — M7, a link whose entity is gone showed its id):
        // reference: f.type === 'ENTITY_REFERENCE'
        //   ? (nameById.get(f.referenceEntityName) ?? f.referenceEntityName) || null
        //   : null,
        reference: f.type === 'ENTITY_REFERENCE'
          ? (nameById.get(f.referenceEntityName) ?? (f.referenceEntityName ? 'an entity that no longer exists' : null))
          : null,
        description: f.description?.trim() || null,
        fields: f.type === 'ARRAY_OF_OBJECT' ? fieldsOf(f.data) : [],
      }));
  return entities
    .filter((e) => !e.hidden)
    // Was (2026-10-03, plan 4a — sample rows in the summary):
    // .map((e) => ({ name: e.name, status: e.status, fields: fieldsOf(e.data) }));
    // Was (2026-10-03, fix round 1 — MINOR B, rowColumns so the summary never prints a leftover key):
    // .map((e) => {
    //   const rows = e.status === 'LOCAL' ? (e.predefinedData ?? []) : [];
    //   return { name: e.name, status: e.status, fields: fieldsOf(e.data), rowCount: rows.length, rows: rows.slice(0, 10) };
    // });
    .map((e) => {
      const rows = e.status === 'LOCAL' ? (e.predefinedData ?? []) : [];
      return {
        name: e.name, status: e.status, fields: fieldsOf(e.data), rowCount: rows.length, rows: rows.slice(0, 10),
        rowColumns: sampleRowFields(e.data).map((f) => f.name),
      };
    });
}

/**
 * The loaded entities with the open entity's UNSAVED edits laid over them — what the builder sees on the
 * page. The open entity replaces its saved copy (same id), or is added when it was never saved (id '').
 * For the model's ENTITIES block and the dock's Tree only; the router keeps using the saved list.
 */
export function entitiesWithOpen(entities: readonly Entity[], open: Entity | null): Entity[] {
  if (!open) return [...entities];
  if (!open.id) return [...entities, open];
  return entities.map((e) => (e.id === open.id ? open : e));
}

/** A cell's text for the summary (plan 4a, MINOR B): flattened to one line, cut to 40 characters. */
function cellText(v: unknown): string {
  const text = (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)).replace(/\n/g, ' ');
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
}

/** One line per entity, then its fields indented two spaces per list level. */
export function renderEntitySummary(summary: readonly SummaryEntity[]): string {
  if (!summary.length) return '(no entities yet)';
  const lines: string[] = [];
  const field = (f: SummaryField, depth: number): void => {
    const ref = f.reference ? ` → ${f.reference}` : '';
    const desc = f.description ? ` — ${f.description}` : '';
    lines.push(`${'  '.repeat(depth)}${f.name}: ${f.type}${ref}${desc}`);
    for (const child of f.fields) field(child, depth + 1);
  };
  for (const e of summary) {
    lines.push(`${e.name} (${e.status})`);
    for (const f of e.fields) field(f, 1);
    if (e.rowCount) {
      // Was (2026-10-03, fix round 1 — MINOR B, only real columns, flattened and cut to size):
      // const shown = e.rows.map((r) => Object.entries(r).map(([k, v]) => `${k}=${v}`).join(' ')).join('; ');
      // Was (2026-10-03, plan 4a final review — rows numbered so the model can fill `rowNumber` without
      // counting):
      // const shown = e.rows
      //   .map((r) => e.rowColumns.filter((c) => c in r).map((c) => `${c}=${cellText(r[c])}`).join(' '))
      //   .join('; ');
      const shown = e.rows
        .map((r, i) => `${i + 1}: ${e.rowColumns.filter((c) => c in r).map((c) => `${c}=${cellText(r[c])}`).join(' ')}`)
        .join('; ');
      lines.push(`  sample rows (${e.rowCount}): ${shown}${e.rowCount > e.rows.length ? '; …' : ''}`);
    }
  }
  return lines.join('\n');
}

const PERMISSION_ORDER: PermissionWord[] = ['create', 'read', 'update', 'delete'];

/** ROLES for the model (plan 4b): each role, then what it may do on each VISIBLE entity, by name — never an id. */
export function renderRoles(roles: readonly RoleInfo[], entities: readonly Entity[]): string {
  if (!roles.length) return '(no roles yet)';
  const nameOf = new Map(entities.filter((e) => !e.hidden).map((e) => [e.id, e.name]));
  return roles
    .map((r) => {
      if (isFullAccessRole(r)) return `${r.name}: full access to every entity (built in)`; // plan 4c (Q37)
      const parts = Object.entries(r.permissions ?? {})
        .filter(([id]) => nameOf.has(id))
        .map(([id, p]) => {
          const can = PERMISSION_ORDER.filter((w) => p?.[w]);
          return `${nameOf.get(id)} (${can.length ? can.join(', ') : 'no permissions'})`;
        });
      return `${r.name}: ${parts.length ? parts.join('; ') : 'no entity permissions'}`;
    })
    .join('\n');
}

/** STANDARD ENTITIES for the model (plan 4b): what can be imported (and what each still needs), then what is imported. */
export function renderOotb(ootb: readonly OotbInfo[]): string {
  if (!ootb.length) return '(none)';
  const byId = new Map(ootb.map((o) => [o.id, o]));
  const notImported = ootb
    .filter((o) => !o.imported)
    .map((o) => {
      const needs = o.deps.filter((d) => !byId.get(d)?.imported).map((d) => byId.get(d)?.name ?? d);
      return needs.length ? `${o.name} (needs ${needs.join(', ')})` : o.name;
    });
  const imported = ootb.filter((o) => o.imported).map((o) => o.name);
  return [
    `not imported: ${notImported.length ? notImported.join('; ') : 'none'}`,
    `imported: ${imported.length ? imported.join(', ') : 'none'}`,
  ].join('\n');
}

/** N18 (plan 4b) — the picked field as it is NOW, so the dock's Properties tab never shows a stale copy. */
export function findSummaryField(
  summary: readonly SummaryEntity[], entity: string, path: readonly string[], name: string,
): SummaryField | null {
  let fields: SummaryField[] | null = summary.find((e) => e.name === entity)?.fields ?? null;
  for (const p of path) fields = fields?.find((f) => f.name === p)?.fields ?? null;
  return fields?.find((f) => f.name === name) ?? null;
}
